import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import css from './Conversation.module.sass';
import { MoveIcon, MoveIconMap, NotStarredMoveIcons, StarredMoveIcons } from './MoveIcons';
import ReactModal from 'react-modal';
import { observer } from 'mobx-react-lite';
import ContentEditable from 'react-contenteditable';
import { observable } from 'mobx';

interface AnnotationSelectorParams {
	isOpen: boolean
	onSelect: (selected?: MoveIcon) => unknown
}

const AnnotationSelector: React.FC<AnnotationSelectorParams> = ({ isOpen, onSelect }) => {
	if (!isOpen) {
		return null
	}

	function MoveIconButtons(icons: MoveIcon[]) {
		return icons.map(icon => {
			const { name, path } = icon

			return <button className={['resetButton', css.selectableIcon].join(' ')} onClick={() => onSelect(icon)}>
				<img src={path} alt={name}/>
			</button>;
		})
	}

	return (
		<ReactModal
			isOpen={isOpen}
			onRequestClose={() => onSelect(undefined)}
			className={css.annotationSelectorModal}
			overlayClassName={css.annotationSelectorModalOverlay}
		>
			<div className={css.annotationSelector}>
				<div className={css.starredAnnotations}>
					{MoveIconButtons(StarredMoveIcons)}
				</div>
				<div className={css.notStarredAnnotations}>
					{MoveIconButtons(NotStarredMoveIcons)}
				</div>
			</div>
		</ReactModal>
	)
}

export interface Message {
	from: 'sender' | 'recipient'
	/**
	 * If content is empty, this message is 'phantom'
	 */
	content: string
	annotation?: MoveIcon,
	isModalOpen?: boolean
}

function focusEnd(el: HTMLElement) {
	// my regards to SO
	// https://stackoverflow.com/questions/4233265/contenteditable-set-caret-at-the-end-of-the-text-cross-browser
	el.focus();
	const range = document.createRange();
	range.selectNodeContents(el);
	range.collapse(false);
	const sel = window.getSelection()!!;
	sel.removeAllRanges();
	sel.addRange(range);
}

const Autofocus: React.FC = ({ children }) => {
	let ref = useRef<any>()

	useEffect(() => focusEnd(ref.current.getEl()), [])

	return React.cloneElement(
		React.Children.only(children) as any,
		{ ref: (el: any) => ref.current = el }
	);
}

export interface ConversationProps {
	store?: Message[]
}

export const makeConversationStore = (value: Message[] = []) => observable<Message>(value)

export const Conversation: React.FC<ConversationProps> = observer(({ store = makeConversationStore() }) => {
	const [phantomBubbleState, setPhantomBubbleState] = useState('none' as 'sender' | 'recipient' | 'none')

	function senderOrRecipientToCss(str: 'sender' | 'recipient') {
		return str === 'sender' ? css.msgSender : css.msgRecipient
	}

	return (
		<div className={css.conversation}>
			{/* Convert each message into a bubble */}
			{store.map((m, i) => {
				const cssClasses = [css.msg, senderOrRecipientToCss(m.from)]
				const buttonClasses = ['resetButton', css.icon]
				if (m.annotation) {
					buttonClasses.push(css.iconVisible)
				}

				return <div className={cssClasses.join(' ')}>
					<div className={css.msgBubble}>
						<Autofocus>
							<ContentEditable html={m.content} onChange={e => m.content = e.target.value}
											 spellCheck={false}/>
						</Autofocus>

						<button
							className={buttonClasses.join(' ')}
							onMouseDown={e => {
								if (e.button === 2) { // remove on right click
									m.annotation = undefined
								} else { // open modal on left click
									m.isModalOpen = true
								}
							}}
							onContextMenu={e => e.preventDefault()}
						>
							<img src={m.annotation?.path ?? MoveIconMap.mistake.path} alt={'select icon'}/>
						</button>

						<button
							className={['resetButton', css.xButton].join(' ')}
							onClick={() => store.splice(i, 1)}>
							<img src={MoveIconMap.incorrect.path} alt={'delete'}/>
						</button>

						<AnnotationSelector
							isOpen={Boolean(m.isModalOpen)}
							onSelect={annotation => {
								m.isModalOpen = false
								m.annotation = annotation
							}}
						/>
					</div>
				</div>
			})}
			<div className={css.phantomBubble}>
				<div
					className={css.leftHoverZone}
					onMouseEnter={() => setPhantomBubbleState('recipient')}
					onMouseLeave={() => setPhantomBubbleState('none')}
					onMouseDown={e => e.preventDefault()}
				/>
				<div
					className={css.rightHoverZone}
					onMouseEnter={() => setPhantomBubbleState('sender')}
					onMouseLeave={() => setPhantomBubbleState('none')}
					onMouseDown={e => e.preventDefault()}
				/>

				{phantomBubbleState !== 'none' &&
				<div
					className={[css.msg, senderOrRecipientToCss(phantomBubbleState)].join(' ')}
				>
					<div className={css.msgBubble}>
						<Autofocus>
							<ContentEditable
								html={''}
								onChange={e => {
									store.push({
										from: phantomBubbleState as 'sender' | 'recipient',
										content: e.target.value
									})
								}}
								spellCheck={false}/>
						</Autofocus>

					</div>

				</div>}
			</div>
		</div>
	);
});
